import type { FeatureKey, UserRole } from '@/types';

/**
 * FRONTEND authorization model. This only decides what the UI shows.
 * It is NOT security: the future Express API MUST re-check role, permission, feature flag and tenant_id
 * on every request (JWT -> tenant context -> `WHERE tenant_id = ctx.tenantId`). Never trust this file.
 */
export const PERMISSIONS = [
  'dashboard.view', 'catalog.read', 'catalog.write', 'inventory.adjust', 'orders.read', 'orders.manage',
  'customers.read', 'marketing.manage', 'wallet.read', 'wallet.adjust', 'loyalty.manage', 'referrals.manage',
  'store.manage', 'analytics.view', 'settings.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const ALL: Permission[] = [...PERMISSIONS];

export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  SUPER_ADMIN: ALL, // platform support access (Step 5 adds the real platform console + impersonation audit)
  TENANT_OWNER: ALL,
  TENANT_ADMIN: ALL,
  STAFF: ['dashboard.view', 'catalog.read', 'catalog.write', 'inventory.adjust', 'orders.read', 'orders.manage', 'customers.read', 'wallet.read'],
  CUSTOMER: [],
};

export const ADMIN_ROLES: UserRole[] = ['SUPER_ADMIN', 'TENANT_OWNER', 'TENANT_ADMIN', 'STAFF'];

export const roleLabel: Record<UserRole, string> = {
  SUPER_ADMIN: 'Platform admin', TENANT_OWNER: 'Store owner', TENANT_ADMIN: 'Store admin', STAFF: 'Staff', CUSTOMER: 'Customer',
};

export const isAdminRole = (role?: UserRole) => !!role && ADMIN_ROLES.includes(role);
export const can = (role: UserRole | undefined, perm: Permission) => !!role && ROLE_PERMISSIONS[role].includes(perm);

/** Which tenant feature (module) a permission's screens depend on, if any. */
export const PERMISSION_FEATURE: Partial<Record<Permission, FeatureKey>> = {
  'wallet.read': 'wallet', 'wallet.adjust': 'wallet', 'loyalty.manage': 'loyalty', 'referrals.manage': 'affiliate',
};

/* --------------------------------------------------------------------------------------------
   PLATFORM permissions (Super Admin console, `/super-admin`). Kept in a separate map from the tenant
   `ROLE_PERMISSIONS` above so nothing about Step 4's merchant admin changes: only SUPER_ADMIN ever gets
   any of these. Still frontend-only — the API must independently verify the caller is platform staff.
-------------------------------------------------------------------------------------------- */
export const PLATFORM_PERMISSIONS = [
  'platform.dashboard.view', 'platform.tenants.read', 'platform.tenants.manage', 'platform.plans.manage',
  'platform.features.manage', 'platform.billing.manage', 'platform.analytics.view', 'platform.audit.view', 'platform.settings.manage',
] as const;
export type PlatformPermission = (typeof PLATFORM_PERMISSIONS)[number];

export const PLATFORM_ROLE_PERMISSIONS: Record<UserRole, PlatformPermission[]> = {
  SUPER_ADMIN: [...PLATFORM_PERMISSIONS], TENANT_OWNER: [], TENANT_ADMIN: [], STAFF: [], CUSTOMER: [],
};

export const isSuperAdmin = (role?: UserRole) => role === 'SUPER_ADMIN';
export const canPlatform = (role: UserRole | undefined, perm: PlatformPermission) => !!role && PLATFORM_ROLE_PERMISSIONS[role].includes(perm);
